// Portal "MY WORK" data layer (spec Part A.1 — Epic 1).
//
// The SINGLE data-access surface for the Portal page. No Portal component
// imports `supabase` directly — every read/write/storage/realtime call lives
// here (Epic 1 acceptance criterion). Modelled on api/conversation.js for the
// shape, but `portalRpc` routes through the existing `rpcReadWithRetry` /
// `wrapSupabaseError` helpers in api/auth.js — `fn_get_portal` is a 160 KB
// aggregator for the 15-park CRR view and a real cold-plan `statement_timeout`
// (57014) risk, exactly like the CDP RPCs (see docs/PORTAL-MY-WORK-EPICS.md
// "Retry resilience" + docs/GUEST-INTELLIGENCE-BACKEND-FOLLOWUPS.md).
//
// Retry policy (binding):
//   - READS (fn_get_portal, fn_portal_drill, fn_portal_conversation) → retried
//     via rpcReadWithRetry on 57014 / transport failure. Idempotent SELECTs.
//   - WRITES (fn_portal_send, fn_portal_request, fn_portal_mark_read) → NEVER
//     retried. A timeout on a write does not say whether it committed.
//
// Raised messages are for the USER — thrown verbatim (Part A.6). The UI shows
// them next to the control, never as a generic "something went wrong".
//
// Mock switch: REACT_APP_PORTAL_STUB=1 (Part H step 1) returns the Part A.10
// payloads from portalMock.js so the whole page builds with no database.

import { supabase } from '../lib/supabase';
import { rpcReadWithRetry } from './auth';
import * as mock from '../components/portal/portalMock';

const PORTAL_STUB =
  typeof process !== 'undefined' &&
  !!process.env &&
  process.env.REACT_APP_PORTAL_STUB === '1';

// CDP-class cold-plan risk → CDP's longer retry schedule (the first timed-out
// scan needs time to page the aggregator's working set into Postgres shared
// buffers before the retry fires). Re-exported so the test can assert it.
export const PORTAL_RETRY_DELAYS_MS = [2000, 4000];

// Success/error envelope — mirrors api/auth.js's `wrapSupabase` / `wrapSupabase`
// exactly. Those are internal consts in auth.js (not exported), so Portal keeps
// its own copy; the shape (`{ error:false, data, message:'ok' }` / labelled
// throw) is the repo standard every call site reads.
const wrapSupabase = (data) => ({ error: false, data, message: 'ok' });
const wrapSupabaseError = (err, label) => {
  const detail = err?.message || 'Supabase RPC failed';
  const message = label ? `${label}: ${detail}` : detail;
  const error = new Error(message);
  error.response = { data: { error: true, message, data: null } };
  throw error;
};

// ─── Core read helper ────────────────────────────────────────────────────────
// Reads go through rpcReadWithRetry (57014 + transport recovery), then the
// repo's standard error envelope: throw on error (labelled), return
// `{ error:false, data, message:'ok' }` on success — matching cdpRpc / convRpc.
async function portalRpc(name, params, label) {
  const { data, error } = await rpcReadWithRetry(name, params, PORTAL_RETRY_DELAYS_MS);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

// ─── Core write helper ───────────────────────────────────────────────────────
// Writes are a SINGLE attempt — never retried (they mutate). Raised messages
// are thrown verbatim (Part A.6): the spec mandates the user sees the exact
// string the database raised, e.g. "not authorised for this park".
async function portalWrite(name, params, label) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

// ─── A.1 exports — reads ─────────────────────────────────────────────────────

// getPortal(parkId) → fn_get_portal({ p_client_id }).
// parkId = null is the all-parks operator view (Part A.3).
export async function getPortal(parkId) {
  if (PORTAL_STUB) {
    if (parkId === null) return wrapSupabase(mock.pageAllParks);
    if (parkId === mock.VERDE) return wrapSupabase(mock.pageEmpty);
    return wrapSupabase(mock.page);
  }
  return portalRpc('fn_get_portal', { p_client_id: parkId }, 'portal page');
}

// portalDrill(what, id, scope, limit) → fn_portal_drill({ p_what, p_id,
// p_scope, p_limit }). `what` ∈ item|person|tracker|history|done_recently.
// `scope` carries { park_id } (the park the drill is anchored to).
export async function portalDrill(what, id, scope = {}, limit = 50) {
  if (PORTAL_STUB) {
    const map = {
      item: mock.drillItem,
      person: mock.drillPerson,
      tracker: mock.drillTracker,
      history: mock.drillHistory,
      done_recently: mock.drillDone,
    };
    return wrapSupabase(map[what] || mock.drillItem);
  }
  return portalRpc(
    'fn_portal_drill',
    { p_what: what, p_id: id, p_scope: scope, p_limit: limit },
    `portal drill (${what})`,
  );
}

// olderMessages(parkId, before, limit) → fn_portal_conversation({ p_client_id,
// p_before, p_limit }). Paginates the conversation rail back in time (Part A.5).
export async function olderMessages(parkId, before, limit = 30) {
  if (PORTAL_STUB) return wrapSupabase(mock.olderMessages);
  return portalRpc(
    'fn_portal_conversation',
    { p_client_id: parkId, p_before: before, p_limit: limit },
    'portal conversation',
  );
}

// ─── A.1 exports — writes (never retried) ────────────────────────────────────

// sendMessage({ parkId, itemId, body, attachmentPath }) → fn_portal_send.
export async function sendMessage({ parkId, itemId, body, attachmentPath = null }) {
  if (PORTAL_STUB) return wrapSupabase(mock.sendResult);
  return portalWrite(
    'fn_portal_send',
    { p_client_id: parkId, p_id: itemId, p_body: body, p_attachment_path: attachmentPath },
    'portal send',
  );
}

// askForSomething({ parkId, title, detail, needBy, files }) → fn_portal_request.
// Creates a new "ask" item (Part A.1 / Epic 10 AskSheet). The real backend
// signature is fn_portal_request(p_client_id, p_title, p_detail, p_need_by,
// p_files) — verified via scripts/_probe-portal-write-existence.js — so this
// maps title/detail/needBy/files directly (NOT a composed p_body). `files` is an
// array of storage paths from uploadFile(); null/[] when no files.
export async function askForSomething({ parkId, title, detail, needBy, files = null }) {
  if (PORTAL_STUB) return wrapSupabase(mock.askResult);
  return portalWrite(
    'fn_portal_request',
    { p_client_id: parkId, p_title: title, p_detail: detail, p_need_by: needBy, p_files: files },
    'portal request',
  );
}

// markRead({ parkId, what, id }) → fn_portal_mark_read. Clears the unread badge
// on a conversation / item (Part A.1 / A.8).
export async function markRead({ parkId, what, id }) {
  if (PORTAL_STUB) return wrapSupabase(mock.markReadResult);
  return portalWrite(
    'fn_portal_mark_read',
    { p_client_id: parkId, p_what: what, p_id: id },
    'portal mark read',
  );
}


// ─── A.7 storage ─────────────────────────────────────────────────────────────
// Private bucket `portal-files`. Path `${parkId}/uploads/${uuid}-${safeName}` —
// the storage policy enforces first-folder = a park the login can open, so the
// path itself is the access check (Part A.1 / A.7).
const PORTAL_BUCKET = 'portal-files';

export async function uploadFile(parkId, file) {
  const safeName = (file?.name || 'upload').replace(/[^a-zA-Z0-9._-]+/g, '_');
  const uuid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  const path = `${parkId}/uploads/${uuid}-${safeName}`;
  const { error } = await supabase.storage
    .from(PORTAL_BUCKET)
    .upload(path, file, { contentType: file?.type || 'application/octet-stream' });
  if (error) wrapSupabaseError(error, 'portal upload');
  return path;
}

// fileUrl(path) → createSignedUrl(path, 3600) — a 1-hour signed URL for a
// previously uploaded file (Part A.1).
export async function fileUrl(path) {
  const { data, error } = await supabase.storage
    .from(PORTAL_BUCKET)
    .createSignedUrl(path, 3600);
  if (error) wrapSupabaseError(error, 'portal file url');
  return data?.signedUrl || null;
}

// ─── A.8 realtime ────────────────────────────────────────────────────────────
// subscribePark(parkId, onEvent) — one private Realtime channel per park;
// broadcasts `message` + `work` events (Part A.8). Returns an unsubscribe.
// `supabase.realtime.setAuth()` is called before subscribe so the channel
// authorises under the current session (RLS on broadcast). Subscription
// failure is non-fatal — the page refreshes on the next load (Part A.8).
export async function subscribePark(parkId, onEvent) {
  let token = null;
  try {
    const { data } = await supabase.auth.getSession();
    token = data?.session?.access_token || null;
  } catch {
    token = null;
  }
  if (token) {
    try {
      supabase.realtime.setAuth(token);
    } catch {
      /* older supabase-js: setAuth may not exist — non-fatal */
    }
  }

  const channel = supabase
    .channel(`portal:${parkId}`)
    .on('broadcast', { event: 'message' }, (payload) => onEvent('message', payload))
    .on('broadcast', { event: 'work' }, (payload) => onEvent('work', payload))
    .subscribe();

  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* already removed — non-fatal */
    }
  };
}

